import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const HL7MappingDialog = ({ open, onClose, onSave, initialMapping = null }) => {
  const { t } = useTranslation();
  
  // JiveX HL7 Mapping Template (nur die Felder, die JiveX erwartet)
  const defaultMapping = {
    patient_name: "PID.5.1",        // patientsName
    patient_id: "PID.3.1",          // patientsID
    patient_birth_date: "PID.7.1",  // patientsBirthDate
    accession_number: "OBR.3.1",    // accessionNumber
    report_text: "OBX.5.1",         // resultText
    report_status: "OBX.11.1",      // resultStatus
    physician: "OBX.16.1"           // physician
  };

  const [mappingData, setMappingData] = useState(
    initialMapping || defaultMapping
  );

  const handleFieldChange = (field, value) => {
    setMappingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(mappingData);
    onClose();
  };

  const handleReset = () => {
    setMappingData(defaultMapping);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('search.hl7SendNodeDialog.title', 'HL7 Feldmapping konfigurieren')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('search.hl7SendNodeDialog.description', 'Konfigurieren Sie das Mapping zwischen Datenbankfeldern und HL7-Segmenten für diesen Sendeknoten.')}
        </Typography>

        {/* Feld-basierte Konfiguration */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {t('search.hl7SendNodeDialog.fieldMapping', 'Feld-Mapping')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Patient Name - patientsName */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.patientName', 'Patient Name')}
                  value={mappingData.patient_name || ''}
                  onChange={(e) => handleFieldChange('patient_name', e.target.value)}
                  placeholder="PID.5.1"
                  size="small"
                  helperText="JiveX: patientsName"
                />
              </Grid>
              
              {/* Patient ID - patientsID */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.patientId', 'Patient ID')}
                  value={mappingData.patient_id || ''}
                  onChange={(e) => handleFieldChange('patient_id', e.target.value)}
                  placeholder="PID.3.1"
                  size="small"
                  helperText="JiveX: patientsID"
                />
              </Grid>
              
              {/* Birth Date - patientsBirthDate */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.patientBirthDate', 'Geburtsdatum')}
                  value={mappingData.patient_birth_date || ''}
                  onChange={(e) => handleFieldChange('patient_birth_date', e.target.value)}
                  placeholder="PID.7.1"
                  size="small"
                  helperText="JiveX: patientsBirthDate"
                />
              </Grid>
              
              {/* Accession Number - accessionNumber */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.accessionNumber', 'Accession Number')}
                  value={mappingData.accession_number || ''}
                  onChange={(e) => handleFieldChange('accession_number', e.target.value)}
                  placeholder="OBR.3.1"
                  size="small"
                  helperText="JiveX: accessionNumber"
                />
              </Grid>
              
              {/* Report Text - resultText */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.reportText', 'Befundtext')}
                  value={mappingData.report_text || ''}
                  onChange={(e) => handleFieldChange('report_text', e.target.value)}
                  placeholder="OBX.5.1"
                  size="small"
                  helperText="JiveX: resultText"
                />
              </Grid>
              
              {/* Report Status - resultStatus */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.reportStatus', 'Befundstatus')}
                  value={mappingData.report_status || ''}
                  onChange={(e) => handleFieldChange('report_status', e.target.value)}
                  placeholder="OBX.11.1"
                  size="small"
                  helperText="JiveX: resultStatus"
                />
              </Grid>
              
              {/* Physician - physician */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('search.hl7SendNodeDialog.fields.physician', 'Arzt')}
                  value={mappingData.physician || ''}
                  onChange={(e) => handleFieldChange('physician', e.target.value)}
                  placeholder="OBX.16.1"
                  size="small"
                  helperText="JiveX: physician"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} className="button-cancel">
          {t('search.hl7SendNodeDialog.reset', 'Zurücksetzen')}
        </Button>
        <Button onClick={onClose} className="button-cancel">
          {t('search.hl7SendNodeDialog.cancel', 'Abbrechen')}
        </Button>
        <Button onClick={handleSave} variant="contained" className="button">
          {t('search.hl7SendNodeDialog.save', 'Speichern')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HL7MappingDialog;
